import { RoutingLink } from "#batteries-included-components/RoutingLink";
import {
  FlowStatusPillVariants,
  useListFlowProductCategories,
  useListFlowProductTypes,
  useListFlowTypes,
} from "#hooks/adapters/useFlows";
import { linkToEquipmentDetail } from "#routes/organization/equipment/[equipmentId]";
import { linkToFacilityDetail } from "#routes/organization/facilities/[facilityId]";
import { FlowDetailRoute } from "#routes/organization/flows/[flowId]/detail";
import useLocalization from "#src/hooks/useLocalization";
import {
  Pill,
  TextDataDisplay,
  type HeaderType,
  type ItemBaseType,
} from "@validereinc/common-components";
import { FlowStatusType, FlowType } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import { get } from "es-toolkit/compat";
import React, { useMemo } from "react";

export const FLOW_ATTRIBUTES = {
  ID: {
    key: "id",
    label: "ID",
  },
  NAME: {
    key: "name",
    label: "Flow",
  },
  TYPE: {
    key: "type",
    label: "Type",
  },
  STATUS: {
    key: "status",
    label: "Status",
  },
  FACILITY: {
    key: "associated_facility_id",
    label: "Facility",
  },
  FACILITY_NAME: {
    key: "associated_facility.name",
    label: "Facility",
  },
  EQUIPMENT: {
    key: "associated_equipment_id",
    label: "Equipment",
  },
  EQUIPMENT_NAME: {
    key: "associated_equipment.name",
    label: "Equipment",
  },
  PRODUCT_CATEGORY: {
    key: "product_category",
    label: "Product Category",
  },
  PRODUCT_TYPE: {
    key: "product_type",
    label: "Product Type",
  },
};

export const useFlowHeaders = <ItemType extends ItemBaseType = FlowType>({
  prefix,
  keySubstitutions,
  sortSubstitutions,
  groupable = false,
}: {
  prefix?: string;
  keySubstitutions?: Record<string, string>;
  sortSubstitutions?: Record<string, string>;
  groupable?: boolean;
} = {}): { isLoading: boolean; headers: Array<HeaderType<ItemType>> } => {
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const { data: flowTypes, isLoading: isLoadingFlowTypes } = useListFlowTypes();
  const { data: productCategories, isLoading: isLoadingFlowProductCategories } =
    useListFlowProductCategories();
  const { data: productTypes, isLoading: isLoadingFlowProductTypes } =
    useListFlowProductTypes();

  const withSortSubstitution = (key: string) =>
    sortSubstitutions?.[key] ?? (prefix ? `${prefix}.${key}` : key);
  const withKeySubstitution = (key: string) =>
    keySubstitutions?.[key] ?? (prefix ? `${prefix}.${key}` : key);

  const headers = useMemo<Array<HeaderType<ItemType>>>(
    () => [
      {
        key: withSortSubstitution(FLOW_ATTRIBUTES.NAME.key),
        label: localize(FLOW_ATTRIBUTES.NAME.label),
        isSortable: true,
        renderComponent: ({ item, opts }) => {
          const id = get(item, withKeySubstitution(FLOW_ATTRIBUTES.ID.key));
          const name = get(item, withKeySubstitution(FLOW_ATTRIBUTES.NAME.key));

          return name && id ? (
            <RoutingLink
              to={FlowDetailRoute.toLink({ pathParams: { flowId: id } })}
            >
              {name}
            </RoutingLink>
          ) : (
            (opts?.emptyText ?? "-")
          );
        },
      },
      {
        key: withSortSubstitution(FLOW_ATTRIBUTES.TYPE.key),
        label: FLOW_ATTRIBUTES.TYPE.label,
        isSortable: true,
        isGroupable: groupable,
        renderComponent: ({ item, opts }) => {
          const type = get(item, withKeySubstitution(FLOW_ATTRIBUTES.TYPE.key));

          return (
            <TextDataDisplay
              value={flowTypes?.data?.find(({ id }) => id === type)?.name}
              {...opts}
            />
          );
        },
      },
      {
        key: withSortSubstitution(FLOW_ATTRIBUTES.FACILITY.key),
        label: localize(FLOW_ATTRIBUTES.FACILITY.label),
        isSortable: true,
        isGroupable: groupable,
        renderComponent: ({ item, opts }) => {
          const facilityId = get(
            item,
            withKeySubstitution(FLOW_ATTRIBUTES.FACILITY.key)
          );
          const facilityName = get(
            item,
            withKeySubstitution(FLOW_ATTRIBUTES.FACILITY_NAME.key)
          );
          return facilityId && facilityName ? (
            <RoutingLink to={linkToFacilityDetail(facilityId)}>
              {facilityName}
            </RoutingLink>
          ) : (
            (opts?.emptyText ?? "-")
          );
        },
      },
      {
        key: withSortSubstitution(FLOW_ATTRIBUTES.EQUIPMENT.key),
        label: localize(FLOW_ATTRIBUTES.EQUIPMENT.label),
        isSortable: true,
        isGroupable: groupable,
        renderComponent: ({ item, opts }) => {
          const equipmentId = get(
            item,
            withKeySubstitution(FLOW_ATTRIBUTES.EQUIPMENT.key)
          );
          const equipmentName = get(
            item,
            withKeySubstitution(FLOW_ATTRIBUTES.EQUIPMENT_NAME.key)
          );
          return equipmentId && equipmentName ? (
            <RoutingLink to={linkToEquipmentDetail(equipmentId)}>
              {equipmentName}
            </RoutingLink>
          ) : (
            (opts?.emptyText ?? "-")
          );
        },
      },
      {
        key: withSortSubstitution(FLOW_ATTRIBUTES.STATUS.key),
        label: FLOW_ATTRIBUTES.STATUS.label,
        isSortable: true,
        isGroupable: groupable,
        renderComponent: ({ item }) => {
          const status = get(
            item,
            withKeySubstitution(FLOW_ATTRIBUTES.STATUS.key)
          ) as FlowStatusType;
          return (
            <Pill variant={FlowStatusPillVariants[status]}>
              {toTitleCaseString(status, {
                intent: "ui",
                exceptions: ["oil-and-gas"],
              })}
            </Pill>
          );
        },
      },
      {
        key: withSortSubstitution(FLOW_ATTRIBUTES.PRODUCT_CATEGORY.key),
        label: FLOW_ATTRIBUTES.PRODUCT_CATEGORY.label,
        isSortable: true,
        isGroupable: groupable,
        renderComponent: ({ item, opts }) => {
          const productCategory = get(
            item,
            withKeySubstitution(FLOW_ATTRIBUTES.PRODUCT_CATEGORY.key)
          );
          return (
            <TextDataDisplay
              value={
                productCategories?.data.find(({ id }) => id === productCategory)
                  ?.name
              }
              {...opts}
            />
          );
        },
      },
      {
        key: withSortSubstitution(FLOW_ATTRIBUTES.PRODUCT_TYPE.key),
        label: FLOW_ATTRIBUTES.PRODUCT_TYPE.label,
        isSortable: true,
        isGroupable: groupable,
        renderComponent: ({ item, opts }) => {
          const productType = get(
            item,
            withKeySubstitution(FLOW_ATTRIBUTES.PRODUCT_TYPE.key)
          );

          return (
            <TextDataDisplay
              value={
                productTypes?.data.find(({ id }) => id === productType)?.name
              }
              {...opts}
            />
          );
        },
      },
    ],
    [
      withSortSubstitution,
      withKeySubstitution,
      localize,
      flowTypes,
      productCategories,
      productTypes,
      groupable,
    ]
  );

  return {
    isLoading:
      isLoadingLocalization ||
      isLoadingFlowTypes ||
      isLoadingFlowProductCategories ||
      isLoadingFlowProductTypes,
    headers,
  };
};
